import { Button, LoadingSpinner, Select } from "@rothoblaas/design-system";
import { FC, useEffect, useRef, useState } from "react";
import { useAuthContext } from "../../../../../context/authContext";
import type { Translation } from "../../../../../hooks/useTranslations";
import {
  getLockCalculatorReportFileName,
  useLockCalculatorReportApi,
} from "../../../../../http/lock_calculator/useReport";
import { getUILanguages, Language } from "../../../../../types";
import type { OrthographicViews } from "../../3d/LockSceneCapture";
import { noticeText } from "../notices/noticeText";
import { useNotices } from "../notices/NoticesContext";
import styles from "./PdfReportSection.module.css";

type Props = {
  calculationId?: string;
  /** Module/calculation/project names used to build the downloaded PDF's filename — each falls back to empty/generic when unset. */
  moduleName?: string | null;
  calculationName?: string | null;
  projectName?: string | null;
  /** Set once a configuration has been chosen and calculated — the report needs both. */
  enabled: boolean;
  /** PNG data-URL captures (panoramica + front/top/2 laterali) from the hidden 3D canvas. Null = still capturing. */
  capturedViews: OrthographicViews | null;
  /** Chiede la cattura delle viste 3D: parte solo al clic su Genera/Scarica, mai a ogni modifica della scena. */
  onRequestCapture: () => void;
  /** A change is waiting to be saved or being saved: the report is built from the saved state, so it waits. */
  hasUnsavedChanges: boolean;
  /** The inputs in force when a generation starts, and the moment its PDF is on screen. */
  onGenerateStart: () => void;
  onGenerated: () => void;
  t: Translation;
};

/** Generates and displays the lock_calculator PDF report on demand, next to the Bill of Materials tab. */
export const PdfReportSection: FC<Props> = ({
  calculationId,
  moduleName,
  calculationName,
  projectName,
  enabled,
  capturedViews,
  onRequestCapture,
  hasUnsavedChanges,
  onGenerateStart,
  onGenerated,
  t,
}) => {
  const auth = useAuthContext();
  const { notices } = useNotices();
  const staleNotice = notices.find((notice) => notice.code === "reportStale");
  const { getLockCalculatorReport, downloadLockCalculatorReport } = useLockCalculatorReportApi();
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const languages = getUILanguages(t);
  const [language, setLanguage] = useState<Language>(
    () => languages.find((l) => l.value === auth.identity?.language) ?? languages[0]!,
  );

  // Azione in attesa delle viste 3D: la cattura parte solo al clic (onRequestCapture),
  // non a ogni modifica della scena, quindi qui si aspetta che arrivi.
  const [pendingAction, setPendingAction] = useState<"generate" | "download" | null>(null);

  const runGenerate = (views: OrthographicViews) => {
    if (!calculationId) return;

    setIsLoading(true);
    setHasError(false);
    setHasGenerated(true);
    setIsFullscreen(false);
    onGenerateStart();

    getLockCalculatorReport(calculationId, views, language.value)
      .then((url) => {
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setReportUrl(url);
        onGenerated();
      })
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  };

  const runDownload = (views: OrthographicViews) => {
    if (!calculationId) return;
    setIsDownloading(true);
    downloadLockCalculatorReport(
      calculationId,
      views,
      language.value,
      getLockCalculatorReportFileName(moduleName, calculationName, projectName),
    )
      .catch(() => setHasError(true))
      .finally(() => setIsDownloading(false));
  };

  // No auto-generation: the report is only (re)built when the user clicks
  // "Genera PDF" (e.g. after choosing a different report language).
  const generateReport = () => {
    if (!calculationId) return;
    if (capturedViews) {
      runGenerate(capturedViews);
      return;
    }
    setIsLoading(true);
    setHasError(false);
    setPendingAction("generate");
    onRequestCapture();
  };

  // Reset to the un-generated state whenever the underlying calculation changes.
  useEffect(() => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    setReportUrl(null);
    setHasError(false);
    setHasGenerated(false);
    setIsFullscreen(false);
    setPendingAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationId]);

  // Le viste sono arrivate: esegue l'azione richiesta con un clic.
  useEffect(() => {
    if (!pendingAction || !capturedViews) return;
    const action = pendingAction;
    setPendingAction(null);
    if (action === "generate") runGenerate(capturedViews);
    else runDownload(capturedViews);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction, capturedViews]);

  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  // Esc closes the full-screen report like its button does (not while the PDF viewer has the focus).
  useEffect(() => {
    if (!isFullscreen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isFullscreen]);

  const handleDownload = () => {
    if (!calculationId) return;
    if (capturedViews) {
      runDownload(capturedViews);
      return;
    }
    setIsDownloading(true);
    setPendingAction("download");
    onRequestCapture();
  };

  if (!enabled) {
    return <div className={styles.placeholder}>{t.lockCalculatorModule.report.empty}</div>;
  }

  const canGenerate = Boolean(calculationId) && !isLoading && !hasUnsavedChanges;
  const isReportShown = Boolean(reportUrl) && hasGenerated && !isLoading && !hasError;
  const savingTitle = hasUnsavedChanges ? t.lockCalculatorModule.report.savingChanges : undefined;

  const languageToolbar = (
    <div className={styles.toolbar}>
      <div className={styles.languageSelectWrapper}>
        <Select<Language, false>
          className={styles.languageSelect}
          options={languages}
          value={language}
          getOptionLabel={(x) => x.display}
          getOptionValue={(x) => x.value}
          onChange={(selected) => selected && setLanguage(selected)}
          isSearchable={false}
        />
      </div>
      <Button variant="primary" disabled={!canGenerate} title={savingTitle} onClick={generateReport}>
        {t.lockCalculatorModule.report.generate}
      </Button>
      <Button
        variant="secondary"
        disabled={!reportUrl || isDownloading || hasUnsavedChanges}
        title={savingTitle}
        onClick={handleDownload}
      >
        {t.lockCalculatorModule.report.download}
      </Button>
      {isReportShown && (
        <div className={styles.fullscreenAction}>
          <Button variant="secondary" onClick={() => setIsFullscreen(!isFullscreen)}>
            {isFullscreen ? t.lockCalculatorModule.report.exitFullscreen : t.lockCalculatorModule.report.fullscreen}
          </Button>
        </div>
      )}
    </div>
  );

  // Nessuno spinner di cattura qui: le viste si catturano solo dopo il clic su
  // Genera/Scarica (isLoading/isDownloading coprono l'attesa), cosi' modificare
  // la scena non fa lampeggiare la sezione ne' ricaricare il viewer del PDF.
  if (isLoading) {
    return (
      <div className={styles.container}>
        {languageToolbar}
        <div className={styles.placeholder}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={styles.container}>
        {languageToolbar}
        <div className={styles.placeholder}>
          <p>{t.lockCalculatorModule.report.error}</p>
          <Button variant="secondary" onClick={generateReport}>
            {t.lockCalculatorModule.report.retry}
          </Button>
        </div>
      </div>
    );
  }

  if (!hasGenerated || !reportUrl) {
    return (
      <div className={styles.container}>
        {languageToolbar}
        <div className={styles.placeholder}>
          <p>{t.lockCalculatorModule.report.notGenerated}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={isFullscreen ? `${styles.container} ${styles.fullscreen}` : styles.container}>
      {languageToolbar}
      {staleNotice && <p className={styles.staleBanner}>{noticeText(staleNotice, t)}</p>}
      {/* Il fragment e' l'unica leva sul viewer PDF del browser: Chrome/Edge
          leggono view=FitH e ignorano /PageLayout, quindi l'adattamento alla
          larghezza va chiesto qui oltre che dentro il documento. Il salvataggio
          nativo del viewer (toolbar=1) resta attivo: usa un nome generico
          perche' il blob URL non ha un filename da suggerirgli — il pulsante
          "Scarica" qui sopra e' l'unico che salva col nome corretto. */}
      <iframe
        className={styles.frame}
        src={`${reportUrl}#view=FitH&toolbar=1`}
        title={t.lockCalculatorModule.report.title}
      />
    </div>
  );
};
